import React, { useEffect, useState } from 'react'
import AddPlaceButton from '@Components/Buttons/AddPlaceButton'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'
import './index.scss'
import ShareUpdateRequestRow from './ShareUpdateRequestRow'
import type { ShareUpdateRow } from './types'
import { getPublisherRowId, isValidShare, publisherToRow, publisherWithShare } from './row-helpers'

interface SongwriterWorkShareUpdateRequestTableProps {
    originalPublishers: OriginalPublisherModel[]
    onAddPublisher: () => void
    onAddAdministrator: (publisher: OriginalPublisherModel) => void
    onSuggestionChanged: (publisher: OriginalPublisherModel | undefined, isValid: boolean) => void
    onAddRow: () => void
    existingPublisherCount: number
}

const SHARE_ERROR_MESSAGE = 'Enter a number between 0.01% - 100.06%'

const createBlankRow = (index: number): ShareUpdateRow => ({
    id: `new-${index}`,
    publisherName: '',
    administratorName: '',
    share: '',
    originalShare: '',
    editing: true,
    isNew: true,
})

const SongwriterWorkShareUpdateRequestTable = ({
    originalPublishers,
    onAddPublisher,
    onAddAdministrator,
    onSuggestionChanged,
    onAddRow,
    existingPublisherCount,
}: SongwriterWorkShareUpdateRequestTableProps) => {
    const [rows, setRows] = useState<ShareUpdateRow[]>([])

    useEffect(() => {
        setRows(originalPublishers.map((publisher, index) => publisherToRow(publisher, index, existingPublisherCount)))
    }, [existingPublisherCount, originalPublishers])

    const publisherForRow = (row: ShareUpdateRow) =>
        originalPublishers.find((publisher, index) => getPublisherRowId(publisher, index) === row.id)
    const isValidSuggestion = (row: ShareUpdateRow, share: string) => {
        const publisher = publisherForRow(row)

        return !!publisher?.publisherName && isValidShare(share)
    }

    const activeEditingRowId = rows.find((row) => row.editing)?.id
    const canAddRow = !activeEditingRowId && !rows.some((row) => row.isNew)

    const cancelEditing = (rowId: string) => {
        setRows((currentRows) =>
            currentRows.map((row) =>
                row.id === rowId ? { ...row, editing: false, share: row.originalShare, shareError: '' } : row
            )
        )
        onSuggestionChanged(undefined, false)
    }

    const startEditing = (row: ShareUpdateRow) => {
        const publisher = publisherForRow(row)
        onSuggestionChanged(
            publisher ? publisherWithShare(publisher, row.share) : undefined,
            isValidSuggestion(row, row.share)
        )
        setRows((currentRows) =>
            currentRows.map((currentRow) => (currentRow.id === row.id ? { ...currentRow, editing: true } : currentRow))
        )
    }

    const toggleEditing = (rowId: string) => {
        const rowToEdit = rows.find((row) => row.id === rowId)
        if (!rowToEdit || (activeEditingRowId && activeEditingRowId !== rowId)) {
            return
        }

        if (rowToEdit.editing) {
            cancelEditing(rowId)
        } else {
            startEditing(rowToEdit)
        }
    }

    const updateShare = (rowId: string, share: string) => {
        const rowToUpdate = rows.find((row) => row.id === rowId)
        const isShareValid = isValidShare(share)
        if (rowToUpdate) {
            const publisher = publisherForRow(rowToUpdate)
            onSuggestionChanged(
                publisher ? publisherWithShare(publisher, share) : undefined,
                isValidSuggestion(rowToUpdate, share)
            )
        }
        setRows((currentRows) =>
            currentRows.map((row) =>
                row.id === rowId ? { ...row, share, shareError: isShareValid ? '' : SHARE_ERROR_MESSAGE } : row
            )
        )
    }

    const addRow = () => {
        if (!canAddRow) {
            return
        }

        setRows((currentRows) => [...currentRows, createBlankRow(currentRows.length)])
        onAddRow()
    }

    return (
        <>
            <table className="songwriter-share-update-request-table">
                <thead>
                    <tr>
                        <th>Publisher</th>
                        <th>Administrator (Optional)</th>
                        <th>Share</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <ShareUpdateRequestRow
                            key={row.id}
                            row={row}
                            activeEditingRowId={activeEditingRowId}
                            publisher={publisherForRow(row)}
                            onAddPublisher={onAddPublisher}
                            onAddAdministrator={onAddAdministrator}
                            onToggleEditing={toggleEditing}
                            onShareChange={updateShare}
                        />
                    ))}
                </tbody>
            </table>
            {canAddRow ? (
                <div className="add-row-button">
                    <AddPlaceButton title="Add “A New Row”" onClick={addRow} />
                </div>
            ) : null}
        </>
    )
}

export default SongwriterWorkShareUpdateRequestTable
